import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PosCustomFeeCalc } from '@mlm/shared';
import {
  getPosCoursePolicy,
  getPosDiscountOffer,
  getPosPaymentMethods,
  getPosPriceMap,
  getPosVouchers,
  getPublicTenantConfig,
  getSlotStockistBranch,
  listBranches,
  listMemberActiveSlots,
  listPosProducts,
  posCheckout,
  type PosCheckoutResult,
  type PosCourseSaleMode,
  type PosPaymentMethodOption,
} from './api';
import { useAuthStore } from './auth-store';
import { useSaleStore } from './cart-store';
import { chargeableFeeRows } from './pos-fees';
import { lineUnitPrice } from './pos-pricing';

/** A fee the cashier picked, carrying its registry `calc` but not yet priced. */
export type SelectedFeeRow = {
  code: string;
  label: string;
  calc: PosCustomFeeCalc;
  amount?: string;
};

/**
 * LMS advisory for a cart holding a course line, or null when there is nothing
 * to say. Shown on the till AND on the checkout page, so the copy lives here
 * rather than being written twice.
 *
 * A till sale always books to the member the cashier selected — there is no
 * anonymous walk-in — so a course grants access to THAT account, commonly a
 * shared house member rather than the person paying. Naming the account is the
 * whole point of the warning; without a member picked there is no name to give
 * and nothing useful to warn about yet.
 */
function courseNoticeFor(mode: PosCourseSaleMode, memberName: string | null): string | null {
  if (mode === 'block') {
    return 'Courses are not sold at the till. Remove it from the cart — the member buys this from the storefront.';
  }
  if (mode === 'warn' && memberName) {
    return `Course access will be granted to ${memberName}. Make sure that is the account that should receive it.`;
  }
  return null;
}

/**
 * Everything the till knows about the sale in progress: the entered state (in
 * `cart-store.ts`, so it survives the move to `/checkout`), the server data it
 * depends on, and the figures derived from both.
 *
 * The till page and the checkout page both call this. Every query is keyed, so
 * the second caller reads the first one's cache and the two can never show
 * different numbers for the same sale.
 */
export function usePosSale({
  onCompleted,
}: {
  /** Called after a successful checkout, once the sale state has been reset. */
  onCompleted?: (receipt: PosCheckoutResult) => void;
} = {}) {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const sale = useSaleStore();
  const { cart, member: selectedMember, slotId: selectedSlotId, fees, amountTendered } = sale;
  // F1 — "Sell to Stockist" mode: the member picker only surfaces stockists, the
  // page reads as a stock transfer, and checkout is blocked unless the selected
  // buyer actually owns a stockist branch. Everything else (cart, pricing,
  // payment, receipt) is the shared POS flow.
  const stockistMode = sale.mode === 'stockist';

  // ── branch context ────────────────────────────────────────────────
  // A scoped cashier auto-uses their branch; an admin / super_admin picks.
  // Always fetched (shared cache with the other pages): drives the picker AND
  // the receipt company header lookup by branch id.
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const { selectedBranchId, selectBranch } = sale;
  useEffect(() => {
    const first = branchesQuery.data?.[0];
    if (!user?.branchId && !selectedBranchId && first) selectBranch(first.id);
  }, [branchesQuery.data, selectedBranchId, selectBranch, user?.branchId]);
  const branchId = user?.branchId ?? selectedBranchId;
  const branch = branchesQuery.data?.find((b) => b.id === branchId) ?? null;

  // ── product catalog ───────────────────────────────────────────────
  const productsQuery = useQuery({
    queryKey: ['pos-products', branchId],
    queryFn: () => listPosProducts(branchId!),
    enabled: !!branchId,
  });

  // ── buyer ─────────────────────────────────────────────────────────
  // Active slots for the selected member; drives the auto-pick / picker logic,
  // the "switch slot" affordance, and the wallet balances shown for the slot.
  //
  // NOT gated on `!selectedSlotId`. That gate meant a member matched BY SLOT
  // CODE never had their list fetched at all, so "switch slot" could not render
  // and the picker would have opened empty — the cashier had to clear the
  // member and start over. It is also what the balances are read from.
  //
  // `staleTime: 0` because the client default is 30s and a balance is the one
  // thing here that a sale changes: ring the same member up twice and the second
  // sale would otherwise quote the pre-sale figure.
  const slotsQuery = useQuery({
    queryKey: ['member-slots', selectedMember?.id],
    queryFn: () => listMemberActiveSlots(selectedMember!.id),
    enabled: !!selectedMember,
    staleTime: 0,
  });
  // Does the SELECTED SLOT own a stockist branch? Only meaningful in stockist
  // mode — the transfer is mode-driven and keyed on the slot the cashier picked
  // (the "Sell to Stockist" search binds the owner slot directly). In normal
  // mode this stays idle so a stockist owner rings up an ordinary sale.
  const stockistQuery = useQuery({
    queryKey: ['slot-stockist', selectedSlotId],
    queryFn: () => getSlotStockistBranch(selectedSlotId!),
    enabled: stockistMode && !!selectedSlotId,
  });
  // Per-buyer discount overlay: the discounted cash price for every product /
  // variant the selected slot's tier / rank / stockist status undercuts. Keyed
  // on the MEMBER as well as the slot — a slotless buyer may still get an
  // overlay (the tenant's non-activated tier), so `null` slot is a real,
  // fetching state and two different members must not share its cache entry.
  // `stockistMode` is part of the KEY, not just the request: the retail tab and
  // the stockist tab price the same member differently, and without it the two
  // would share one cache entry and show each other's prices.
  const priceMapQuery = useQuery({
    queryKey: ['pos-price-map', branchId, selectedMember?.id ?? null, selectedSlotId, stockistMode],
    queryFn: () => getPosPriceMap(branchId!, selectedMember!.id, selectedSlotId, stockistMode),
    enabled: !!branchId && !!selectedMember,
  });
  const priceMap = selectedMember ? (priceMapQuery.data ?? null) : null;

  // A member with exactly one slot gets it without being asked. With more than
  // one the till has to ask (`needsSlotPick`); the page owns that dialog.
  const slots = slotsQuery.data;
  const { setSlot } = sale;
  useEffect(() => {
    if (!selectedMember || selectedSlotId || !slots) return;
    if (slots.length === 1) setSlot(slots[0]!.id, slots[0]!.slotCode);
  }, [selectedMember, selectedSlotId, slots, setSlot]);
  const needsSlotPick = !!selectedMember && !selectedSlotId && (slots?.length ?? 0) > 1;

  // The selected slot's row from the list — this is where the balances live.
  // A fallback rather than an assertion: the list is a separate fetch and is
  // briefly absent right after a slot-code match binds `selectedSlotId`.
  const selectedSlot = (slots ?? []).find((s) => s.id === selectedSlotId) ?? null;

  // ── LMS course lines ──────────────────────────────────────────────
  // Whose account a course lands on is decided by the member picker, so the
  // till says so out loud before charging. Defaults to the server's own
  // default while the policy is in flight — an advisory that turns out to be
  // unnecessary is harmless; a missing one is the bug this exists to fix.
  const coursePolicyQ = useQuery({ queryKey: ['pos-course-policy'], queryFn: getPosCoursePolicy });
  const courseMode = coursePolicyQ.data ?? 'warn';
  const cartHasCourse = cart.some((l) => l.product.isCourse);
  // UX only — /pos/checkout rejects a blocked course sale on its own.
  const courseBlocked = cartHasCourse && courseMode === 'block';
  const courseNotice = cartHasCourse
    ? courseNoticeFor(courseMode, selectedMember?.fullName ?? selectedMember?.email ?? null)
    : null;

  // ── discounts ─────────────────────────────────────────────────────
  // What this branch lets the till offer: the preset buttons and whether any
  // typed code is good here. Not asked for on the stockist tab: a transfer is a
  // restock, and the server refuses a coupon on one.
  const discountOfferQ = useQuery({
    queryKey: ['pos-discount-offer', branchId],
    queryFn: () => getPosDiscountOffer(branchId!),
    enabled: !!branchId && !stockistMode,
  });
  const discountPresets = stockistMode ? [] : (discountOfferQ.data?.presets ?? []);
  const promoCodesEnabled = !stockistMode && (discountOfferQ.data?.codesEnabled ?? false);
  // The buyer's gift checks (cashier phase 4), offered beside the presets. They
  // are the buyer's own, so the branch's discount mode does not gate them.
  // `staleTime: 0` because a sale uses one up. Same config cache as the theme.
  const tenantConfigQ = useQuery({
    queryKey: ['public-tenant-config'],
    queryFn: getPublicTenantConfig,
    staleTime: 60_000,
    retry: false,
  });
  const vouchersEnabled = !stockistMode && tenantConfigQ.data?.vouchersEnabled === true;
  const voucherLabel = tenantConfigQ.data?.voucherLabel ?? 'Gift Check';
  const vouchersQ = useQuery({
    queryKey: ['pos-vouchers', selectedMember?.id],
    queryFn: () => getPosVouchers(selectedMember!.id),
    enabled: vouchersEnabled && !!selectedMember,
    staleTime: 0,
  });
  const buyerVouchers = vouchersEnabled && selectedMember ? (vouchersQ.data?.vouchers ?? []) : [];
  /**
   * The coupon as the preview and the checkout send it, for a sale paid in
   * `currency`. A coupon is a peso discount: a GC / coin sale carries none, so
   * switching to such a tender simply leaves it out (and back in again when the
   * cashier returns to cash) instead of failing the sale.
   */
  const couponRequest = (
    currency: 'cash' | 'gc' | 'coin',
  ): { discountPresetId?: string; couponCode?: string; voucherId?: string } => {
    const c = sale.coupon;
    if (!c || stockistMode || currency !== 'cash') return {};
    if (c.kind === 'preset') return { discountPresetId: c.id };
    if (c.kind === 'voucher') return { voucherId: c.id };
    return { couponCode: c.code };
  };

  // ── totals ────────────────────────────────────────────────────────
  const goodsTotal = useMemo(
    () => cart.reduce((sum, l) => sum + lineUnitPrice(l, priceMap) * l.qty, 0),
    [cart, priceMap],
  );

  // F1 CMS: the tender options come from the admin-managed `pos_payment_methods`
  // registry (not a hardcoded list). The selected method's `tenderKind` drives
  // cash-with-change / exact (no change) / wallet-debit behavior. Declared ahead
  // of the fees below, because the selected method owns its fee list.
  const methodsQ = useQuery({ queryKey: ['pos-payment-methods'], queryFn: getPosPaymentMethods });
  // Memoized so the `?? []` fallback isn't a fresh array on every render — it is
  // a dependency of the default-method effect below, which would otherwise re-run
  // (and re-assert the default) on every single render.
  const methods = useMemo(() => methodsQ.data ?? [], [methodsQ.data]);
  // Which scrip wallets this tenant can actually be paid in. A balance for a
  // tender nobody accepts is noise; a tender with no balance shown is the blind
  // spot this feature exists to remove.
  const offersGc = methods.some((m) => m.currency === 'gc');
  const offersCoin = methods.some((m) => m.currency === 'coin');
  const selectedMethodCode = sale.methodCode;
  const selectedMethod = methods.find((m) => m.code === selectedMethodCode) ?? null;
  const tenderKind = selectedMethod?.tenderKind ?? 'cash';

  // Default the selected method once the registry loads, and recover if the
  // current pick becomes invalid (disabled / removed).
  const { setMethodCode } = sale;
  useEffect(() => {
    if (methods.length === 0) return;
    if (!selectedMethod) setMethodCode(methods[0]!.code);
  }, [methods, selectedMethod, setMethodCode]);

  // Charges added on top of the goods (e.g. "Delivery" ₱50) — never discounted,
  // never PV-bearing; the server applies them after the manager discount.
  // Cash-denominated only; a gc/coin tender clears them below.
  //
  // The TYPE comes from the admin registry and only its code is sent — the
  // server resolves the label, which is what stops six tills spelling "Delivery"
  // six ways. The amount is still per-sale (a delivery fee varies by distance).
  //
  // Each payment method carries its OWN fee list (Card may add a surcharge Cash
  // doesn't), and the server resolves against the method being paid with.
  // Memoized so the fallback `[]` stays referentially stable for the effect below.
  const feeTypes = useMemo(() => selectedMethod?.fees ?? [], [selectedMethod]);
  // Switching tender switches the fee list: drop any row whose type the new
  // method doesn't carry, or checkout would 400 `unknown_custom_fee_type`.
  // Waits for a method to be selected: before that `feeTypes` is empty only
  // because nothing is picked yet, not because the method carries no fees.
  const { setFees } = sale;
  useEffect(() => {
    if (!selectedMethod) return;
    setFees((fs) => {
      const kept = fs.filter((f) => feeTypes.some((t) => t.code === f.typeCode));
      return kept.length === fs.length ? fs : kept;
    });
  }, [feeTypes, selectedMethod, setFees]);
  const feeLabelOf = (code: string) => feeTypes.find((t) => t.code === code)?.label ?? code;
  const feeCalcFor = (code: string): PosCustomFeeCalc =>
    feeTypes.find((t) => t.code === code)?.calc ?? { kind: 'manual' };
  /** What a computed fee charges, shown where the amount box would be. */
  const feeRateLabel = (code: string) => {
    const calc = feeCalcFor(code);
    if (calc.kind === 'percent') return `${calc.value}%`;
    if (calc.kind === 'flat') return `₱${calc.value.toFixed(2)}`;
    return '';
  };
  /** Types not already on another row — one line per type, so a type is offered once. */
  const availableFeeTypes = (rowCode: string) =>
    feeTypes.filter((t) => t.code === rowCode || !fees.some((f) => f.typeCode === t.code));
  const addFeeRow = () => {
    const next = feeTypes.find((t) => !fees.some((f) => f.typeCode === t.code));
    if (next) setFees((fs) => [...fs, { typeCode: next.code, amount: '' }]);
  };
  const updateFee = (i: number, patch: Partial<{ typeCode: string; amount: string }>) =>
    setFees((fs) => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeFee = (i: number) => setFees((fs) => fs.filter((_, idx) => idx !== i));

  // ── tender ────────────────────────────────────────────────────────
  // Per-currency order total from the cart; null if any line lacks a price in
  // that currency (so the currency can't be used for this cart). A variant
  // line uses the VARIANT's flat gc/coin price (null = not sold in it).
  function currencyTotal(cur: 'gc' | 'coin'): number | null {
    let sum = 0;
    for (const l of cart) {
      const src = l.variant ?? l.product;
      const raw = cur === 'gc' ? src.priceGc : src.priceCoin;
      if (raw == null) return null;
      sum += Number(raw) * l.qty;
    }
    return sum;
  }
  const gcTotal = currencyTotal('gc');
  const coinTotal = currencyTotal('coin');
  // A wallet method debits the buyer's balance in its currency. gc/coin use the
  // flat per-currency cart total; a cash-wallet method pays the NORMAL cash total
  // from the member's cash balance (discounts + PV + comp apply as usual).
  const walletCurrency: 'cash' | 'gc' | 'coin' | null =
    tenderKind === 'wallet' ? ((selectedMethod?.currency as 'cash' | 'gc' | 'coin') ?? null) : null;
  const nonCash = tenderKind === 'wallet';
  // A fee is a peso service charge — it can't be denominated in GC/coin, so a
  // scrip tender hides the field and drops any amount already typed. A CASH
  // wallet tender still takes it (the member pays goods + fee from their cash
  // balance, exactly like drawer cash).
  const customFeeAllowed = walletCurrency !== 'gc' && walletCurrency !== 'coin';
  // A GC / coin tender pays the flat per-currency cart total; when a line has
  // no price in that currency there is nothing to debit.
  const scripTotal = walletCurrency === 'gc' ? gcTotal : walletCurrency === 'coin' ? coinTotal : 0;
  const tenderUnpriceable = scripTotal == null;
  // The rows that will actually be charged, in the registry order the server
  // prices them in. See `chargeableFeeRows` for both rules.
  const chargeableFees = customFeeAllowed ? chargeableFeeRows(fees, feeTypes) : [];
  /**
   * The fees to charge, in registry order, labelled and carrying each type's
   * `calc`, but UNPRICED: a percent fee is charged on the goods AFTER the
   * manager discount, so only the screen that knows the discount can price
   * them (`checkout-totals.ts`).
   */
  const selectedFees: SelectedFeeRow[] = chargeableFees.map((f) => ({
    code: f.typeCode,
    label: feeLabelOf(f.typeCode),
    calc: feeCalcFor(f.typeCode),
    amount: f.amount,
  }));
  // The server REJECTS an amount sent for a computed type (it prices those from
  // the registry itself), so send the code alone for those.
  const customFeesPayload =
    chargeableFees.length > 0
      ? chargeableFees.map((f) =>
          feeCalcFor(f.typeCode).kind === 'manual'
            ? { typeCode: f.typeCode, amount: f.amount }
            : { typeCode: f.typeCode },
        )
      : undefined;
  const feeAmountInvalid = customFeeAllowed && fees.some((f) => Number(f.amount) < 0);

  // A gc/coin wallet method is unusable when the cart isn't fully priced in its
  // currency. A cash-wallet method always uses the cash total, so it's never
  // priceability-disabled (the server rejects on insufficient balance instead).
  function methodDisabled(m: PosPaymentMethodOption): boolean {
    if (m.tenderKind !== 'wallet' || m.currency === 'cash') return false;
    return currencyTotal(m.currency as 'gc' | 'coin') == null;
  }

  // ── checkout ──────────────────────────────────────────────────────
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const { completeSale, setCharging } = sale;
  const checkout = useMutation({
    mutationFn: posCheckout,
    meta: { successMessage: 'Sale completed.' },
    // The in-flight flag lives in the store, not in this mutation: the cart now
    // survives leaving the page, so a cashier who walks away mid-charge and
    // comes back gets a NEW instance of this hook whose own `isPending` is
    // false. These callbacks belong to the mutation itself and still run after
    // the page that started it has unmounted.
    onMutate: () => setCharging(true),
    onSettled: () => setCharging(false),
    onSuccess: (data) => {
      setCheckoutError(null);
      completeSale(data);
      queryClient.invalidateQueries({ queryKey: ['pos-products', branchId] });
      // The sale may have debited a wallet, so the cached balances are stale.
      queryClient.invalidateQueries({ queryKey: ['member-slots'] });
      // And used up a gift check, or completed the purchase that earned one.
      queryClient.invalidateQueries({ queryKey: ['pos-vouchers'] });
      onCompleted?.(data);
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response
        ?.data;
      setCheckoutError(data?.error ?? data?.message ?? 'checkout_failed');
    },
  });

  function buildManagerDiscount():
    | { pct?: number; amount?: number; reason?: string; approvalChip?: string }
    | undefined {
    const md = sale.managerDiscount;
    const pct = Number(md.pct);
    const amount = Number(md.amount);
    if (!pct && !amount) return undefined;
    return {
      pct: pct || undefined,
      amount: amount || undefined,
      reason: md.reason.trim() || undefined,
      approvalChip: md.approvalChip ?? undefined,
    };
  }

  /**
   * Charge the sale. `amountDue` is the final total the checkout page worked
   * out (`checkout-totals.ts`): goods after line pricing, less the cashier
   * discount, plus fees. It is what an exact (card) method tenders, because the
   * server requires that tender to EQUAL the order total, and what a cash
   * tender is measured against.
   */
  function submitCheckout(amountDue: string) {
    if (!branchId || !selectedMember || cart.length === 0) return;
    if (!selectedMethodCode) {
      setCheckoutError('no_payment_method');
      return;
    }
    const items = cart.map((l) => ({
      productId: l.product.id,
      variantId: l.variant?.id,
      qty: l.qty,
    }));
    const managerDiscount = buildManagerDiscount();
    const notes = sale.remarks.trim() || undefined;
    const coupon = couponRequest(nonCash ? (walletCurrency ?? 'cash') : 'cash');
    const couponFields = {
      ...coupon,
      couponApprovalChip: coupon.discountPresetId ? (sale.couponChip ?? undefined) : undefined,
    };

    if (sale.charging) return;
    // One key per sale (`cart-store.ts`): if this request is sent twice, the
    // server returns the first order instead of charging again.
    const idempotencyKey = sale.saleKey;

    if (nonCash) {
      if (tenderUnpriceable) {
        setCheckoutError(`product_not_purchasable_in_${walletCurrency}`);
        return;
      }
      setCheckoutError(null);
      // The backend recomputes + debits the total in the wallet's currency;
      // amountTendered is informational for non-cash.
      checkout.mutate({
        branchId,
        buyerUserId: selectedMember.id,
        buyerSlotId: selectedSlotId,
        items,
        payment: {
          method: 'cash',
          currency: walletCurrency!,
          amountTendered: walletCurrency === 'cash' ? amountDue : String(scripTotal),
          paymentMethodCode: selectedMethodCode,
          idempotencyKey,
        },
        notes,
        managerDiscount,
        ...couponFields,
        // undefined for gc/coin by construction — a scrip order can't carry a
        // peso fee, and the server rejects one outright.
        customFees: customFeesPayload,
        stockistTransfer: stockistMode,
      });
      return;
    }

    // Exact (card) tenders the amount due with no change; cash takes the entered bill.
    const tenderForExact = tenderKind === 'exact' ? amountDue : amountTendered;
    if (!tenderForExact || !(Number(tenderForExact) >= Number(amountDue))) {
      setCheckoutError('insufficient_payment');
      return;
    }
    setCheckoutError(null);
    checkout.mutate({
      branchId,
      buyerUserId: selectedMember.id,
      buyerSlotId: selectedSlotId,
      items,
      payment: {
        method: tenderKind === 'exact' ? 'card' : 'cash',
        currency: 'cash',
        amountTendered: tenderForExact,
        paymentMethodCode: selectedMethodCode,
        idempotencyKey,
      },
      notes,
      managerDiscount,
      ...couponFields,
      customFees: customFeesPayload,
      stockistTransfer: stockistMode,
    });
  }

  return {
    // entered state + its setters (see cart-store.ts)
    ...sale,
    stockistMode,
    // branch
    user,
    branchId,
    branch,
    branches: branchesQuery.data ?? [],
    branchesLoading: branchesQuery.isLoading,
    // catalog
    products: productsQuery.data,
    productsLoading: productsQuery.isLoading,
    priceMap,
    // buyer
    slots: slots ?? [],
    selectedSlot,
    needsSlotPick,
    stockistBranch: stockistQuery.data ?? null,
    stockistBranchLoading: stockistQuery.isLoading,
    // course advisory
    courseBlocked,
    courseNotice,
    // payment methods + fees
    methods,
    methodsLoading: methodsQ.isLoading,
    offersGc,
    offersCoin,
    selectedMethod,
    tenderKind,
    methodDisabled,
    feeTypes,
    feeLabelOf,
    feeCalcFor,
    feeRateLabel,
    availableFeeTypes,
    addFeeRow,
    updateFee,
    removeFee,
    customFeeAllowed,
    feeAmountInvalid,
    selectedFees,
    // discounts
    discountPresets,
    promoCodesEnabled,
    couponRequest,
    vouchersEnabled,
    voucherLabel,
    buyerVouchers,
    // totals + tender
    goodsTotal,
    tenderUnpriceable,
    walletCurrency,
    nonCash,
    // checkout
    submitCheckout,
    checkoutPending: sale.charging,
    checkoutError,
    setCheckoutError,
  };
}

export type PosSale = ReturnType<typeof usePosSale>;
